import { makeStyles, tokens } from '@coreai/fluentui-react';
import { customScrollbarStyles } from '@/styles/scrollbar';

/**
 * Styles for {@link EnvironmentVariablesTab} and its inner `EnvVarRow`.
 * Extracted from the component file to keep the TSX focused on behavior.
 */
export const useEnvironmentVariablesTabStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalM,
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  searchInput: {
    minWidth: '220px',
    flex: '1 1 220px',
    maxWidth: '360px',
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalXS,
    marginLeft: 'auto',
  },

  // Table wrapper — layered shadows for depth instead of hard border.
  // Padding-block-end gives the last row breathing room above the rounded
  // bottom edge so the row doesn't kiss the radius.
  tableWrapper: {
    flex: '0 1 auto',
    maxHeight: '100%',
    overflow: 'auto',
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: `0px 0px 0px 1px ${tokens.colorNeutralStroke2}, ${tokens.shadow4}`,
    backgroundColor: tokens.colorNeutralBackground1,
    paddingBlockEnd: tokens.spacingVerticalS,
    ...customScrollbarStyles,
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    // Required by `useTableColumnSizing_unstable` so the column widths it
    // injects via inline `style.width` are honored by the browser layout.
    tableLayout: 'fixed',
  },

  // Header — stronger visual weight
  headerRow: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  headerCell: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    letterSpacing: '0.02em',
    paddingBlock: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':first-child': {
      paddingInlineStart: tokens.spacingHorizontalXL,
    },
    ':last-child': {
      paddingInlineEnd: tokens.spacingHorizontalXL,
    },
  },

  // Column width is owned by `useTableColumnSizing_unstable` (see COLUMN_SIZING
  // in the component). The Actions column still needs class-level rules to
  // right-align its trailing buttons.
  actionsColumn: {
    '& > button, & > div': {
      justifyContent: 'flex-end',
      paddingInlineEnd: '0px',
    },
  },

  // Rows — smooth hover with alternating tint for scannability.
  // The `:hover` / `:focus-within` selectors also reveal any descendant
  // marked with `data-hover-reveal` (the copy buttons) so the row reads
  // as a quiet, scannable list until the user engages with it. The
  // edit/delete/eye action buttons stay visible at all times so users
  // never have to hunt for them.
  row: {
    transitionProperty: 'background-color, box-shadow',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      boxShadow: `inset 3px 0 0 0 ${tokens.colorBrandStroke2}`,
    },
    ':nth-child(even)': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    ':nth-child(even):hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
    ':hover [data-hover-reveal]': {
      opacity: 1,
    },
    // `:has(:focus-visible)` instead of `:focus-within` so the copy button
    // only re-appears when the row holds *keyboard* focus. Mouse-clicking
    // any row button (edit, eye, delete) leaves DOM focus on that button
    // — with `:focus-within` that would persistently reveal the copy
    // buttons after a click, which read as a stuck-hover bug.
    ':has(:focus-visible) [data-hover-reveal]': {
      opacity: 1,
    },
  },
  rowDeleted: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    textDecorationLine: 'line-through',
    opacity: 0.6,
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveEasyEase,
    animationName: {
      from: { opacity: 1 },
      to: { opacity: 0.6 },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      animationDuration: '0.01ms',
    },
    ':hover': {
      backgroundColor: tokens.colorPaletteRedBackground1,
      boxShadow: 'none',
    },
    ':nth-child(even)': {
      backgroundColor: tokens.colorPaletteRedBackground1,
    },
  },
  rowNew: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    ':hover': {
      backgroundColor: tokens.colorPaletteGreenBackground1,
      boxShadow: `inset 3px 0 0 0 ${tokens.colorPaletteGreenBorder1}`,
    },
    ':nth-child(even)': {
      backgroundColor: tokens.colorPaletteGreenBackground1,
    },
  },
  rowChanged: {
    boxShadow: `inset 3px 0 0 0 ${tokens.colorBrandStroke1}`,
    ':hover': {
      boxShadow: `inset 3px 0 0 0 ${tokens.colorBrandStroke1}`,
    },
  },
  // Editing row — elevated with subtle highlight
  rowEditing: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    boxShadow: `inset 3px 0 0 0 ${tokens.colorBrandStroke1}, ${tokens.shadow2}`,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Selected,
      boxShadow: `inset 3px 0 0 0 ${tokens.colorBrandStroke1}, ${tokens.shadow2}`,
    },
    ':nth-child(even)': {
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
  },

  // Jump-to-existing destination flash. A brief brand-colored highlight
  // pulses the row so the user's eye is drawn to the destination after
  // scrolling. The `!important`-less overrides below win over the static
  // rowChanged/rowEditing/etc. variants because composed class order
  // places rowFlash last (see getRowClass in the component).
  rowFlash: {
    // Use the longest Fluent motion token. (No hardcoded duration values —
    // see portal/AGENTS.md motion tokens guidance.) The pulse is still
    // clearly perceptible and falls within the motion system's scale.
    animationDuration: tokens.durationUltraSlow,
    animationTimingFunction: tokens.curveEasyEase,
    animationName: {
      '0%': { backgroundColor: tokens.colorBrandBackground2 },
      '30%': { backgroundColor: tokens.colorBrandBackground2 },
      '100%': { backgroundColor: 'transparent' },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      animationDuration: '0.01ms',
    },
  },

  // Cells — generous vertical padding for breathing room; first/last cells get
  // extra inline space so content doesn't hug the rounded table edges.
  cell: {
    verticalAlign: 'middle',
    paddingBlock: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    ':first-child': {
      paddingInlineStart: tokens.spacingHorizontalXL,
    },
    ':last-child': {
      paddingInlineEnd: tokens.spacingHorizontalXL,
    },
  },
  // Top-align cells in edit mode so inputs share the same baseline across columns
  // even when one column has a Field hint or validation message underneath.
  // Extra top padding keeps the inputs from hugging the row edge.
  editingCell: {
    verticalAlign: 'top',
    paddingBlockStart: tokens.spacingVerticalM,
  },
  cellInput: {
    width: '100%',
    borderRadius: tokens.borderRadiusMedium,
    transitionProperty: 'border-color, box-shadow',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
  },
  // View-mode cell text (Name and Value). These are NOT clickable — editing
  // is driven exclusively by the pencil icon in the Actions column, matching
  // Azure Portal / Vercel / Netlify conventions for sensitive data. Leaving
  // the text non-interactive also lets users select-and-copy values directly.
  cellText: {
    fontSize: tokens.fontSizeBase300,
    wordBreak: 'break-all',
  },
  // Monospace so the dots kern identically on every row; small letterspacing for
  // readability without widening the glyph box. `foreground2` (not 4) because
  // the dots *are* the masked value — they carry meaning and must meet WCAG AA
  // (4.5:1) in both themes. `foreground4` fails AA in dark mode.
  maskedText: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    letterSpacing: '0.05em',
    userSelect: 'none',
    ':hover': {
      textDecoration: 'none',
    },
  },
  dialogButton: {
    whiteSpace: 'nowrap',
  },
  // Value cell layout: the text fills the available column width and the
  // trailing action buttons (eye toggle, copy) are anchored to the right.
  // Without `flex: 1` on the text the cell sized to content, so the eye
  // button physically jumped left/right as the value text switched between
  // the 10-bullet mask and the revealed string — visually jarring.
  valueCell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    width: '100%',
    minWidth: 0,
  },
  valueText: {
    // `display: block` is required for `text-overflow: ellipsis` to take
    // effect — Fluent's `<Text>` renders a `<span>` which is inline by
    // default, and inline elements ignore `text-overflow`.
    display: 'block',
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Action buttons — expand hit area with pseudo-element for min 40×40px
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalXXS,
  },
  validationError: {
    // Match Fluent `<Field validationMessage>`: icon-aligned red text at
    // fontSizeBase200. Keeps view-mode duplicate errors visually identical
    // to edit-mode errors (which Fluent's Field renders for us).
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    paddingTop: tokens.spacingVerticalXXS,
  },
  validationErrorIcon: {
    flexShrink: 0,
    color: tokens.colorPaletteRedForeground1,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXXL,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  emptyStateHeading: {
    color: tokens.colorNeutralForeground1,
  },
  emptyStateBody: {
    maxWidth: '420px',
    color: tokens.colorNeutralForeground3,
  },
  emptyStateActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
    paddingTop: tokens.spacingVerticalS,
  },
  emptyIcon: {
    fontSize: tokens.fontSizeHero900,
    color: tokens.colorNeutralForeground3,
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
  },
  // Skeleton row cells — match the real row layout for no-shift loading
  skeletonCell: {
    paddingBlock: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
  },
  skeletonItem: {
    height: '20px',
    borderRadius: tokens.borderRadiusMedium,
  },
  skeletonItemShort: {
    width: '60%',
  },
  skeletonItemLong: {
    width: '85%',
  },
  skeletonItemActions: {
    width: '72px',
    marginInlineStart: 'auto',
  },

  // Sensitive row decorations

  errorBar: {
    maxWidth: '100%',
    overflow: 'hidden',
  },
  errorBody: {
    wordBreak: 'break-word',
  },
  statusBar: {
    marginBlockEnd: tokens.spacingVerticalM,
    // Sanitized error messages can still be long (e.g., a full sentence
    // describing what failed). Wrap them rather than overflowing the bar.
    wordBreak: 'break-word',
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveEasyEase,
    animationName: {
      from: { opacity: 0, transform: 'translateY(-6px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      animationDuration: '0.01ms',
    },
  },
  statusBarBody: {
    display: 'inline',
  },
  // Tabular numerals for the unsaved-changes count so the title doesn't
  // shift width as the count ticks 1 → 2 → 10 (the previous bespoke
  // footer already did this; the unified bar lost it in the refactor).
  statusBarCount: {
    fontVariantNumeric: 'tabular-nums',
  },
  popoverContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
  },
  popoverActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },

  // Animations
  rowSlideIn: {
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveEasyEase,
    animationName: {
      from: { opacity: 0, transform: 'translateY(-8px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      animationDuration: '0.01ms',
    },
  },
  footerSlideUp: {
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveEasyEase,
    animationName: {
      from: { opacity: 0, transform: 'translateY(-12px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      animationDuration: '0.01ms',
    },
  },

  // Status text (replaces inline styles)
  // NOTE: `colorNeutralForeground4` fails WCAG AA in dark mode (~3.2:1 on
  // neutral bg). Use `foreground3` (~6.8:1) for body text that must remain
  // readable in both themes. Reserve `foreground4` for disabled states only.
  emptyText: {
    color: tokens.colorNeutralForeground3,
  },

  // Inline error block (with optional "Jump to existing" link)
  errorGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalXXS,
  },
  jumpLink: {
    alignSelf: 'flex-start',
  },
  // A bit of breathing room below the name field group (hint, and the
  // optional "Jump to existing" duplicate link) so the bottom edge of
  // whichever content is rendered doesn't kiss the row below.
  nameFieldGroup: {
    paddingBottom: tokens.spacingVerticalS,
  },
  // Copy-reference button — small, subtle, aligned with key name.
  // Hidden by default and revealed on row hover/focus (see the `row` rule
  // which targets `[data-hover-reveal]`). On touch devices `:hover` is
  // unreliable, so we fall back to always-visible via `@media (hover: none)`.
  // Explicit foreground2 override: Fluent's `subtle` + `size="small"` defaults
  // to foreground3 for icon color, which looks pale in dark mode. foreground2
  // (~10.5:1 on dark neutral) lifts the affordance without making it loud.
  copyButton: {
    flexShrink: 0,
    minWidth: 'auto',
    color: tokens.colorNeutralForeground2,
    opacity: 0,
    transitionProperty: 'opacity, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
    ':focus-visible': {
      opacity: 1,
    },
    '@media (hover: none)': {
      opacity: 1,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  // Row action buttons (edit / delete / eye toggle / undo) — always
  // visible. Explicit foreground2 override: Fluent's `subtle` + `size="small"`
  // defaults to foreground3 for icon color, which looks pale in dark mode.
  // foreground2 (~10.5:1 on dark neutral) lifts the affordance without
  // making it loud.
  rowActionButton: {
    color: tokens.colorNeutralForeground2,
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  // Visually hidden status region for announcing "Copied!" to assistive tech
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  errorCount: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
  },
});
